/**
 * Configuration options for the FetchService.
 * Represents the options that can be passed to any HTTP method.
 */
export interface FetchConfig extends RequestInit {
  /**
   * Query parameters to append to the URL.
   * Automatically serialized to a query string.
   */
  queryParams?: Record<string, string | number | boolean | undefined | null>;

  /**
   * Request timeout in milliseconds.
   * Defaults to 10000ms (10 seconds) if not specified.
   */
  timeout?: number;

  /**
   * When true will return the raw response
   */
  returnRawResponse?: boolean;

  /**
   * Listener to track download progress.
   * If passed, the request expect a readable body as response
   */
  onDownloadProgress?: (event: CustomProgressEvent) => void;

  headers?: Headers;
}

export type RequestInterceptor = (config: FetchConfig) => FetchConfig | Promise<FetchConfig>;
export type ResponseInterceptor = <T>(data: T, rawResponse: Response) => T | Promise<T>;

export type CustomProgressEvent = {
  loaded: number;
  total: number;
  progress: number;
};
export type UploadOptions = Omit<RequestInit, 'body'> & {
  body?: Document | XMLHttpRequestBodyInit | null;
  onProgress?: (event: CustomProgressEvent) => void;
};

export class FetchService {
  private readonly baseUrl: string;

  /**
   * Default configuration applied to all requests.
   * Can be overridden per request.
   */
  protected defaultConfig: FetchConfig = {
    timeout: 10000,
    headers: new Headers({}),
  };

  protected requestInterceptors: RequestInterceptor[] = [];
  protected responseInterceptors: ResponseInterceptor[] = [];

  constructor(baseUrl: string) {
    // Remove trailing slashes
    this.baseUrl = baseUrl.replace(/\/$/, '');
  }

  getBaseUrl() {
    return this.baseUrl;
  }

  addRequestInterceptor(interceptor: RequestInterceptor): this {
    this.requestInterceptors.push(interceptor);
    return this;
  }

  addResponseInterceptor(interceptor: ResponseInterceptor): this {
    this.responseInterceptors.push(interceptor);
    return this;
  }

  private async applyRequestInterceptors(config: FetchConfig): Promise<FetchConfig> {
    let currentConfig = config;
    for (const interceptor of this.requestInterceptors) {
      currentConfig = await interceptor(currentConfig);
    }
    return currentConfig;
  }

  private async applyResponseInterceptors<T>(data: T, rawResponse: Response): Promise<T> {
    let currentData = data;
    for (const interceptor of this.responseInterceptors) {
      currentData = await interceptor(currentData, rawResponse);
    }
    return currentData;
  }

  /**
   * Serializes an object of parameters into a URL query string.
   * Handles null, undefined, and empty values by skipping them.
   *
   * @example
   * ```typescript
   * const qs = this.serializeParams({ page: 1, limit: 10 });
   * // Returns: 'page=1&limit=10'
   * ```
   */
  private serializeParams(params: Record<string, any> | undefined): string {
    if (!params) return '';

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    return searchParams.toString();
  }

  /**
   * Builds the final URL with query parameters.
   */
  private buildUrl(path: string, params?: Record<string, any>): string {
    const queryString = this.serializeParams(params);
    const separator = queryString ? (path.includes('?') ? '&' : '?') : '';
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${normalizedPath}${separator}${queryString}`;
  }

  /**
   * The core fetch method.
   *
   * @param path - The API endpoint path
   *
   */
  private async fetch<R = unknown>(path: string, config?: FetchConfig): Promise<R> {
    const finalConfig: FetchConfig = { ...this.defaultConfig, ...config };

    const configWithInterceptors = await this.applyRequestInterceptors(finalConfig);
    const { returnRawResponse, timeout, queryParams, onDownloadProgress, ...fetchConfig } = configWithInterceptors;
    const url = this.buildUrl(path, queryParams);

    let controller: AbortController | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (timeout && timeout > 0) {
      controller = new AbortController();
      timeoutId = setTimeout(() => controller?.abort(), timeout);
    }

    // In case we already passed a signal
    const signals: AbortSignal[] = [];
    if (fetchConfig.signal) signals.push(fetchConfig.signal);
    if (controller) signals.push(controller.signal);
    // If AbortSignal.any is supported (baseline in 2024), the request will be canceled when any of the signal is aborted.
    // Otherwise the cancellation depends on the provided custom signal (if any), otherwise the signal related to the timeout
    const finalSignal = signals.length > 0 ? (AbortSignal.any ? AbortSignal.any(signals) : signals[0]) : undefined;

    try {
      let rawResponse = await fetch(url, {
        ...fetchConfig,
        signal: finalSignal,
      });

      if (onDownloadProgress && rawResponse.body) {
        const contentLength =
          rawResponse.headers.get('x-uncompressed-content-length') || rawResponse.headers.get('content-length');
        const total = contentLength ? parseInt(contentLength, 10) : 0;
        let loaded = 0;

        const reader = rawResponse.body.getReader();
        const chunks: Uint8Array<ArrayBuffer>[] = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          if (value) {
            chunks.push(value);
            loaded += value.byteLength;
          }

          onDownloadProgress({
            loaded,
            total,
            // We set the progress to -1 to indicate we don't have a content-length header in the response
            progress: total > 0 ? loaded / total : -1,
          });
        }

        // Reconstruct rawResponse from the buffered chunks
        const contentType = rawResponse.headers.get('content-type') || '';
        const blob = new Blob(chunks, { type: contentType });

        rawResponse = new Response(blob, {
          status: rawResponse.status,
          statusText: rawResponse.statusText,
          headers: rawResponse.headers,
        });
      }

      // Returns the raw response if specified
      if (returnRawResponse) return rawResponse as R;

      // Read the Content-Type header to determine how to parse the body
      const contentType = rawResponse.headers.get('content-type');
      let responseData: any;

      // Handle "No Content" explicitly
      if (rawResponse.status === 204) {
        responseData = undefined;
      }
      // Handle JSON
      else if (contentType?.includes('application/json')) {
        const text = await rawResponse.text();
        try {
          responseData = text ? JSON.parse(text) : undefined;
        } catch (err) {
          throw new Error(`Invalid JSON response: ${(err as Error).message}. Text: ${text}`);
        }
      }
      // Handle Binary Data (files, images)
      else if (
        contentType?.includes('application/octet-stream') ||
        contentType?.includes('application/pdf') ||
        contentType?.includes('image/')
      ) {
        responseData = await rawResponse.blob();
      }
      // Fallback for text/plain, text/html, text/csv, etc.
      else {
        responseData = await rawResponse.text();
      }

      const dataWithInterceptors = await this.applyResponseInterceptors(responseData, rawResponse);

      return dataWithInterceptors;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Parses raw XHR headers into a Fetch API Headers object.
   */
  private parseXhrHeaders(rawHeaders: string): Headers {
    const headers = new Headers();
    if (!rawHeaders) return headers;

    // XHR headers are separated by CR-LF (\r\n)
    const lines = rawHeaders.trim().split(/[\r\n]+/);
    for (const line of lines) {
      const parts = line.split(': ');
      const name = parts.shift();
      const value = parts.join(': ');
      if (name && value) {
        headers.append(name, value);
      }
    }

    return headers;
  }

  /**
   * Uploads data using XHR to track progress, but returns a Fetch Response object.
   */
  async uploadWithProgress(path: string, options: UploadOptions = {}): Promise<Response> {
    const { onProgress, method = 'POST', headers, body, ...restOptions } = options;

    return new Promise<Response>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, this.buildUrl(path));

      // Apply fetch-like RequestInit options
      if (restOptions.credentials === 'include') {
        xhr.withCredentials = true;
      }

      // Apply headers
      if (headers) {
        const headersObj = new Headers(headers);
        headersObj.forEach((value, key) => {
          xhr.setRequestHeader(key, value);
        });
      }

      // Attach upload progress listener
      if (xhr.upload && onProgress) {
        xhr.upload.addEventListener('progress', (event: ProgressEvent<XMLHttpRequestEventTarget>) => {
          if (event.lengthComputable) {
            onProgress({
              loaded: event.loaded,
              total: event.total,
              progress: event.loaded / event.total,
            });
          }
        });
      }

      // Handle resolution
      xhr.onload = () => {
        const responseHeaders = this.parseXhrHeaders(xhr.getAllResponseHeaders());

        resolve(
          new Response(xhr.response, {
            status: xhr.status,
            statusText: xhr.statusText,
            headers: responseHeaders,
          }),
        );
      };

      // Handle rejections
      xhr.onerror = () => reject(new TypeError('Network request failed'));
      xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
      xhr.ontimeout = () => reject(new TypeError('Network request timed out'));

      xhr.send(body ?? null);
    });
  }

  get<R = unknown>(path: string, config?: FetchConfig): Promise<R> {
    return this.fetch<R>(path, { ...config, headers: new Headers(config?.headers), method: 'GET' });
  }

  post<R = unknown, Body = any>(path: string, body?: Body, config?: FetchConfig): Promise<R> {
    const isFormData = body instanceof FormData;
    const headers = new Headers(config?.headers);
    if (!isFormData && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    return this.fetch<R>(path, {
      ...config,
      method: 'POST',
      body: isFormData ? body : JSON.stringify(body),
      headers,
    });
  }

  put<R = unknown, Body = any>(path: string, body?: Body, config?: FetchConfig): Promise<R> {
    const isFormData = body instanceof FormData;
    const headers = new Headers(config?.headers);
    if (!isFormData && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    return this.fetch<R>(path, {
      ...config,
      method: 'PUT',
      body: isFormData ? body : JSON.stringify(body),
      headers,
    });
  }

  patch<R = unknown, Body = any>(path: string, body?: Body, config?: FetchConfig): Promise<R> {
    const isFormData = body instanceof FormData;
    const headers = new Headers(config?.headers);
    if (!isFormData && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    return this.fetch<R>(path, {
      ...config,
      method: 'PATCH',
      body: isFormData ? body : JSON.stringify(body),
      headers,
    });
  }

  delete<R = unknown, Body = any>(path: string, body?: Body, config?: FetchConfig): Promise<R> {
    const headers = new Headers(config?.headers);
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    return this.fetch<R>(path, {
      ...config,
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
      headers,
    });
  }
}
